import * as store from '@attain-apps/fe/web/main/lib/store'
import * as rxjs from 'rxjs'

import type { Collection } from '../../../../collection'
import type * as ecModels from '../../../../models'
import type { Plugin } from '../plugin'

/**
 * Deletes an entity
 */
export const executeDelete = store.effects.create(
  <TTypeParams extends ecModels.TypeParams.Constraint>(
    feature: Plugin<TTypeParams>['feature'],
    collection: Collection<TTypeParams>,
  ) => {
    const canDelete = collection.plugins.apiExecutionPlugin.canExecute('delete')
    const canBatchDelete =
      collection.plugins.apiExecutionPlugin.canExecute('batchDelete')
    if (!canDelete && !canBatchDelete) {
      // TODO: automatically cancel every request
      return undefined
    }

    return feature.actions.delete.relay((forwardTo, { ids }) =>
      collection.feature.selectors.context.asObservable.pipe(
        // NOTE: we only want to take the first context. This is really
        // important since otherwise the relay method does not know when we are
        // done sending instructions
        rxjs.take(1),
        rxjs.exhaustMap(context => {
          if ((ids.length > 1 && canBatchDelete) || !canDelete) {
            // If this is a batch (more than one), and we can batch delete, or
            // if we can't delete so we have no other option than to batch
            // delete, then we just submit one batch delete request
            return rxjs.of(
              forwardTo(
                collection.plugins.apiExecutionPlugin.feature.actions.execute,
                {
                  input: {
                    args: [ids],
                    context,
                    methodName: 'batchDelete',
                  },
                },
              ),
            )
          } else {
            // Otherwise, we submit a delete request for each id
            return rxjs.from(ids).pipe(
              rxjs.map(id =>
                forwardTo(
                  collection.plugins.apiExecutionPlugin.feature.actions.execute,
                  {
                    input: {
                      args: [id],
                      context,
                      methodName: 'delete',
                    },
                  },
                ),
              ),
            )
          }
        }),
      ),
    ).asObservable
  },
)
